import type { ImageModel } from 'ai';
import {
    BaseImageProvider,
    type ImageProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getKieModelConfig } from './utils';
import { kieApiPaths, kieStatusMapping } from './config';
import type { KieCreateResponse, KieTaskResponse } from './types';
import { getValueByPath, isStatusMatch } from '../tools';

// 创建提供商实例
const provider = new BaseImageProvider(
    {
        baseUrlEnv: 'KIE_AI_BASE_URL',
        apiKeyEnv: 'KIE_AI_API_KEY',
        debugEnv: 'KIE_AI_DEBUG',
        providerName: 'KIE',
    },
    {
        timeoutMs: 10 * 60 * 1000,
        backoff: {
            initialDelayMs: 5000,
            maxSteps: 20,
            scale: 1,
            jitter: 0.2,
            maxDelayMs: 10000,
        },
    }
);

// 实现适配器
const adapter: ImageProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 构建输入参数 (可能是异步构建)
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 返回请求配置
        return {
            url: `${context.baseUrl}${kieApiPaths.createTask}`,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
            body: payload,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: KieCreateResponse): CreateTaskResult {
        // 获取任务 ID
        const taskId = getValueByPath(data, kieStatusMapping.taskIdPath);
        if (!taskId) {
            try {
                // 补充响应体与状态日志，便于定位后端返回结构问题
                // 注意：仅记录必要字段，避免日志过大
                const status = (response as any)?.status;
                const contentType = (response as any)?.headers?.get ? (response as any).headers.get('content-type') : undefined;
                // 尝试提取常见错误字段
                const brief =
                    typeof data === 'object'
                        ? {
                              taskIdPath: kieStatusMapping.taskIdPath,
                              message: (data as any)?.message || (data as any)?.error || undefined,
                              code: (data as any)?.code || (data as any)?.status || undefined,
                              keys: Object.keys(data as any).slice(0, 10),
                          }
                        : { note: 'data is not object' };
                // eslint-disable-next-line no-console
                console.error('[KIE][create:missingTaskId]', { modelId, status, contentType, brief, data });
            } catch {}
            throw new Error(`Failed to get task id from response`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 返回请求配置
        return {
            url: (taskId) => `${context.baseUrl}${kieApiPaths.pollTask}?taskId=${taskId}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: KieTaskResponse): PollTaskResult {
        // 获取任务状态
        const state = getValueByPath(data, kieStatusMapping.statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }
        // 1. 判断**成功**状态
        if (isStatusMatch(state, kieStatusMapping.success)) {
            // 获取结果字符串
            const resultJsonStr = getValueByPath(data, kieStatusMapping.imageUrlPath);
            // 结果字符串不存在或不是字符串则视为**失败**状态
            if (!resultJsonStr || typeof resultJsonStr !== 'string') {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing resultJson in response',
                    raw: data,
                };
            }
            // 解析结果 JSON (JSON to Object)
            let resultJson: { resultUrls: string[] };
            try {
                resultJson = JSON.parse(resultJsonStr);
            } catch (e) {
                // 解析失败则视为**失败**状态
                return {
                    status: TaskStatus.FAILED,
                    error: 'Invalid resultJson format',
                    raw: data,
                };
            }
            // 解析图像 URL
            const imageUrls = Array.isArray(resultJson.resultUrls) ? resultJson.resultUrls : undefined;
            // 图像 URL 不存在则视为**失败**状态
            if (!imageUrls || imageUrls.length === 0) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing image URLs in result',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                imageUrls: imageUrls,
                raw: data,
            };
        }

        // 2. 判断**失败**状态
        if (isStatusMatch(state, kieStatusMapping.failed)) {
            const error = getValueByPath(data, kieStatusMapping.errorPath);
            // 返回**失败**状态
            return {
                status: TaskStatus.FAILED,
                error: String(error) || 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, kieStatusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 判断**未知**状态 (不在配置范围内)
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const kie = {
    image: (modelId: string): ImageModel => {
        const config = getKieModelConfig(modelId);
        const timeoutMs = config?.timeoutMs;
        return provider.createImageModel(modelId, adapter, timeoutMs);
    },
};